/**
 * The predecessors column: what a task waits for, editable in place.
 *
 * Project has you type `12FS+3d` into a cell. That is fast once you know it and opaque
 * before then, and it means knowing a row number — which is exactly the thing this
 * product does not make people do. So the cell opens instead: one line per predecessor,
 * with the task, the kind of link and the lag each on their own control, and a line at
 * the bottom for adding another.
 *
 * The vocabulary of link types comes from the server, for the same reason the
 * constraints do — a relation the API accepts and this screen does not offer is a
 * feature nobody can reach, and the reverse is a dropdown that fails on save.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Menu, MenuDivider, MenuLabel, PortalSelect } from '../../components/overlay'
import type { Predecessor, Row } from './plan'

type Choice = { value: string; label: string; hint: string }

export function useRelations(projectId: string) {
  return useQuery({
    queryKey: ['schedule-relations', projectId],
    queryFn: async () => {
      const meta = await api.get<{ relations: Choice[] }>(`/projects/${projectId}/schedule/meta`)
      return meta.relations
    },
    staleTime: 60 * 60 * 1000,
  })
}

/** `CUP-2`, or `CUP-2 SS`, or `CUP-2 FS+2d` — the shorthand every planner reads. */
export function shorthand(link: Predecessor): string {
  const lag = link.lag_days === 0 ? '' : link.lag_days > 0 ? `+${link.lag_days}d` : `${link.lag_days}d`
  return `${link.key}${link.relation === 'FS' && !lag ? '' : ` ${link.relation}${lag}`}`
}

export function PredecessorCell({
  projectId,
  row,
  rows,
  writable,
  onLink,
  onUnlink,
}: {
  projectId: string
  row: Row
  rows: Row[]
  writable: boolean
  onLink: (input: { from: string; to: string; relation: string; lag: number }) => void
  onUnlink: (from: string, to: string) => void
}) {
  const links = row.predecessors ?? []
  const summary = links.map(shorthand).join(', ')

  if (!writable) {
    return (
      <span className="block truncate font-mono text-[11px] text-[var(--color-ink-dim)]">
        {summary}
      </span>
    )
  }

  return (
    <Menu
      width={330}
      label={`Predecessors of ${row.key}`}
      trigger={(props) => (
        <button
          type="button"
          {...props}
          className="w-full truncate text-left font-mono text-[11px] text-[var(--color-ink-dim)]"
          title={summary || 'Nothing yet — click to say what this waits for'}
        >
          {summary || <span className="text-[var(--color-ink-faint)]">—</span>}
        </button>
      )}
    >
      {() => (
        <Editor
          projectId={projectId}
          row={row}
          rows={rows}
          links={links}
          onLink={onLink}
          onUnlink={onUnlink}
        />
      )}
    </Menu>
  )
}

function Editor({
  projectId,
  row,
  rows,
  links,
  onLink,
  onUnlink,
}: {
  projectId: string
  row: Row
  rows: Row[]
  links: Predecessor[]
  onLink: (input: { from: string; to: string; relation: string; lag: number }) => void
  onUnlink: (from: string, to: string) => void
}) {
  const relations = useRelations(projectId)

  // The hint is the two-letter code, not the server's sentence. The list puts hints in
  // a fixed column on the right, so a sentence there squeezes the label down to
  // "Finis…" and the option becomes unreadable — and the code is the more useful thing
  // to show anyway, because it is what the column itself displays.
  const options = (relations.data ?? []).map((r) => ({
    value: r.value,
    label: r.label,
    hint: r.value,
  }))

  // Everything except itself, its own descendants, and what it already waits for. A
  // task offered as its own predecessor is a loop the server would refuse anyway, and
  // offering it is how somebody finds that out the hard way.
  const taken = new Set(links.map((l) => l.key))
  const banned = descendants(row, rows)
  // The hint carries both names a person might use: the number in the grid's own #
  // column, which is what somebody means by "task 22", and the key, which is what the
  // cell displays. Searching either finds the row.
  const candidates = rows
    .filter((r) => r.key !== row.key && !taken.has(r.key) && !banned.has(r.key) && !r.has_children)
    .map((r) => ({ value: r.key, label: r.title, hint: `${r.order + 1} · ${r.key}` }))

  return (
    <div className="min-w-0">
      <MenuLabel>Predecessors — what {row.key} waits for</MenuLabel>

      {links.length === 0 && (
        <p className="px-3 pb-1 text-[12px] text-[var(--color-ink-faint)]">
          Nothing yet. It can start whenever its own rules allow.
        </p>
      )}

      {links.map((link) => (
        <div key={link.id} className="flex items-center gap-1.5 px-3 py-1">
          <span className="w-16 shrink-0 truncate font-mono text-[11px]" title={link.key}>
            {link.key}
          </span>
          <span className="min-w-0 flex-1">
            <PortalSelect
              options={options}
              value={link.relation}
              clearable={false}
              searchable={false}
              ariaLabel={`How ${row.key} waits for ${link.key}`}
              panelWidth={280}
              onChange={(v) =>
                onLink({ from: link.key, to: row.key, relation: v ?? 'FS', lag: link.lag_days })
              }
            />
          </span>
          <input
            type="number"
            defaultValue={link.lag_days}
            aria-label="Days of lag"
            title="Days to wait after the link is met. Negative overlaps them."
            className="w-12 rounded-[3px] border border-[var(--color-rule)] bg-[var(--color-surface)] px-1 py-1 text-right text-[12px] tabular-nums outline-none"
            onBlur={(e) => {
              const days = Number(e.target.value) || 0
              if (days !== link.lag_days) {
                onLink({ from: link.key, to: row.key, relation: link.relation, lag: days })
              }
            }}
          />
          <button
            type="button"
            aria-label={`Stop waiting for ${link.key}`}
            title={`Stop waiting for ${link.key}`}
            className="shrink-0 px-1 text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]"
            onClick={() => onUnlink(link.key, row.key)}
          >
            ×
          </button>
        </div>
      ))}

      <MenuDivider />
      <MenuLabel>Add one</MenuLabel>
      {/* Picking the task is the whole gesture. It used to need a second press on an
          "Add" button below, and the thing people did was pick, see nothing change, and
          conclude the picker was broken. The link is made finish-to-start the moment a
          task is chosen; the kind and the lag are then on its own line above, where a
          change to them is a change to that link and nothing else. */}
      <div className="flex items-center gap-1.5 px-3 py-1">
        <span className="min-w-0 flex-1">
          <PortalSelect
            options={candidates}
            value={null}
            placeholder={candidates.length ? 'Pick a task, by name or number…' : 'Nothing left to wait for'}
            ariaLabel="The task this one waits for"
            panelWidth={300}
            disabled={!candidates.length}
            onChange={(v) => {
              if (v) onLink({ from: v, to: row.key, relation: 'FS', lag: 0 })
            }}
          />
        </span>
      </div>
      <p className="px-3 pt-0.5 pb-2 text-[11px] text-[var(--color-ink-faint)]">
        Linked finish-to-start as soon as you pick. Change the kind or the days of lag on its line.
      </p>
    </div>
  )
}

/**
 * A task and everything under it, by key.
 *
 * Waiting for your own child is a loop with extra steps: the child's dates roll up into
 * yours, so the link would be asking the task to wait for itself.
 */
export function descendants(row: Row, rows: Row[]): Set<string> {
  const byParent = new Map<string | null, Row[]>()
  for (const r of rows) {
    const list = byParent.get(r.parent_id) ?? []
    list.push(r)
    byParent.set(r.parent_id, list)
  }
  const out = new Set<string>([row.key])
  const queue = [row]
  while (queue.length) {
    const current = queue.shift()!
    for (const child of byParent.get(current.id) ?? []) {
      if (out.has(child.key)) continue
      out.add(child.key)
      queue.push(child)
    }
  }
  return out
}
